import { TicketFontCatalogEntry } from "../shared/interfaces/ticket-font";
import { listTicketFontsRequest } from "../axios/routes/ticketFonts";

const loadedFaceKeys = new Set<string>();

let catalogCache: TicketFontCatalogEntry[] | null = null;
let catalogPromise: Promise<TicketFontCatalogEntry[]> | null = null;

export function invalidateTicketFontCatalog(): void {
  catalogCache = null;
  catalogPromise = null;
}

export async function getTicketFontCatalog(): Promise<TicketFontCatalogEntry[]> {
  if (catalogCache) {
    return catalogCache;
  }
  if (!catalogPromise) {
    catalogPromise = listTicketFontsRequest()
      .then((catalog) => {
        catalogCache = catalog;
        return catalog;
      })
      .catch((err) => {
        catalogPromise = null;
        throw err;
      });
  }
  return catalogPromise;
}

function faceKey(family: string, weight: number, style: string): string {
  return `${family}|${weight}|${style || "normal"}`;
}

export async function ensureTicketFontFaces(
  catalog: TicketFontCatalogEntry[],
  cssFamilies: string[],
): Promise<void> {
  if (typeof document === "undefined" || !document.fonts) {
    return;
  }
  const wanted = new Set(cssFamilies.filter(Boolean));
  const uploads = catalog.filter((f) => (
    wanted.has(f.cssFamily) && f.source === "uploaded" && f.variants?.length
  ));
  await Promise.all(uploads.flatMap((font) => (
    (font.variants || []).map(async (variant) => {
      if (!variant.url) {
        return;
      }
      const key = faceKey(font.cssFamily, variant.weight, variant.style);
      if (loadedFaceKeys.has(key)) {
        return;
      }
      const face = new FontFace(
        font.cssFamily,
        `url(${variant.url})`,
        { weight: String(variant.weight), style: variant.style || "normal" },
      );
      await face.load();
      document.fonts.add(face);
      loadedFaceKeys.add(key);
    })
  )));
}

export async function ensureTemplateFontsForFamilies(families: string[]): Promise<void> {
  const unique = Array.from(new Set(families.filter(Boolean)));
  if (!unique.length) {
    return;
  }
  const catalog = await getTicketFontCatalog();
  await ensureTicketFontFaces(catalog, unique);
}
